import { apiReturnPaginatedResults, apiReturnResults } from '../../apiFactory/apiReturn';
import { Request } from '../../common/api';
import {
    type ApiResult,
    type BaseParams,
    type CallParams,
    type Competition,
    type CompetitionCollectionAvailabilityResponse,
    type DiscoverCollectionResponse,
    type FilterCompetitionParams,
    type FreshCollectionResponse,
    type FreshIdeasResponse,
    type LeaderboardItem,
    type PaginatedResult,
    type PagingParams,
    type ResourceId,
} from '../../common/structs';
import { links } from '../../links';

export type GetCompetitionsParams = BaseParams<PagingParams> & FilterCompetitionParams;
type FantasyLeaderboardParams = BaseParams<PagingParams> & { slug: ResourceId };
type CollectionValidityParams = CallParams<{ competitionId: ResourceId; slugs?: string[] }>;
type GetCollectionsParams = CallParams<{ slug: ResourceId }>;
type FreshCollectionCheckParams = CallParams<{ competitionSlug: ResourceId }>;
type FreshIdeasParams = CallParams<{ competitionSlug: ResourceId }>;
type GameDetailsParams = CallParams<{ slug: string }>;

export async function getCompetitionList(
    params: GetCompetitionsParams,
): Promise<ApiResult<PaginatedResult<Competition>>> {
    return Request.getRequest<PaginatedResult<Competition>>({
        ...params,
        target: links.competitions.collection,
        settings: {
            ...params.settings,
        },
    });
}
export async function getLeaderBoardCompetitionWishlists(
    params: FantasyLeaderboardParams,
): Promise<ApiResult<PaginatedResult<LeaderboardItem>>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.single(params.slug).leaderBoard(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}
export async function getCompetitionCollectionValidity(
    params: CollectionValidityParams,
): Promise<ApiResult<CompetitionCollectionAvailabilityResponse>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.collectionAvailability(params.competitionId),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}
export async function getCompetition(params: GameDetailsParams): Promise<ApiResult<Competition>> {
    return Request.getRequest<Competition>({
        ...params,
        target: links.competitions.single(params.slug).competition,
    });
}
export async function getTopCollections(params: GetCollectionsParams): Promise<ApiResult<DiscoverCollectionResponse>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.single(params.slug).topCollections(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}

export async function getRecentCollections(
    params: GetCollectionsParams,
): Promise<ApiResult<DiscoverCollectionResponse>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.single(params.slug).recentCollections(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}
export const callCompetitionCollectionValidity = (params: CollectionValidityParams) =>
    apiReturnResults<CompetitionCollectionAvailabilityResponse>(getCompetitionCollectionValidity, params);
export const callLeaderboardCompetitionWishlist = (params: FantasyLeaderboardParams) =>
    apiReturnPaginatedResults<LeaderboardItem>(getLeaderBoardCompetitionWishlists, params);

export const callGetCompetition = (params: GameDetailsParams) => apiReturnResults<Competition>(getCompetition, params);
export const callGetCompetitions = (params: GetCompetitionsParams) =>
    apiReturnPaginatedResults<Competition>(getCompetitionList, params);

export const callTopCollections = (params: GetCollectionsParams) =>
    apiReturnResults<DiscoverCollectionResponse>(getTopCollections, params);

export const callRecentCollections = (params: GetCollectionsParams) =>
    apiReturnResults<DiscoverCollectionResponse>(getRecentCollections, params);

export async function getIsFreshPick(params: FreshCollectionCheckParams): Promise<ApiResult<FreshCollectionResponse>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.single(params.competitionSlug).freshPick(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}

export const callIsFreshPick = (params: FreshCollectionCheckParams) =>
    apiReturnResults<FreshCollectionResponse>(getIsFreshPick, params);

export async function getFreshIdeas(params: FreshIdeasParams): Promise<ApiResult<FreshIdeasResponse>> {
    return Request.getRequest({
        ...params,
        target: links.competitions.single(params.competitionSlug).freshIdeas(),
        settings: {
            ...params.settings,
            credentials: 'omit',
            ignoreHeaders: ['Authorization'],
        },
    });
}

export const callGetFreshIdeas = (params: FreshIdeasParams) =>
    apiReturnResults<FreshIdeasResponse>(getFreshIdeas, params);
